(ns renolit-roofing-service.layout.notifications
  (:require [cljc.java-time.instant :as jt.instant]
            [cljs.reader :as reader]
            [re-frame.core :as rf]
            [renolit-roofing-service.entity-list :as entity-list]
            [renolit-roofing-service.layout.dropdown-menu :as layout.dropdown-menu]
            [renolit-roofing-service.layout.icon :as layout.icon]
            [renolit-roofing-service.localization :refer [t]]
            [renolit-roofing-service.navigation :as nav]
            [renolit-roofing-service.service.notification :as srv.notification]
            [renolit-roofing-service.shared.util.time :as shared.time]
            [renolit-roofing-service.util.interop :as util.interop]))

(def ^:private entity-key :notifications)

(def ^:private trigger->icon-key
  {:service-requested :file})

(defn- icon-key-for-trigger
  [trigger]
  (get trigger->icon-key trigger :file))

(defn- decode-message
  "Notification messages are persisted as the `pr-str` of a hiccup vector
  (see `notification/websocket.clj`'s `notification->persistence-notification`),
  so they need to be read back into data before they can be rendered."
  [message]
  (try
    (reader/read-string message)
    (catch :default _
      message)))

(defn- ->date
  [instant]
  (js/Date. (jt.instant/to-epoch-milli instant)))

(defn- same-calendar-day?
  [a b]
  (and (= (.getFullYear a) (.getFullYear b))
       (= (.getMonth a) (.getMonth b))
       (= (.getDate a) (.getDate b))))

(defn- day-group-label
  [created-at]
  (let [created (->date created-at)
        now (js/Date.)
        yesterday (js/Date. (- (.getTime now) (* 24 60 60 1000)))]
    (cond
      (same-calendar-day? created now) (t [:notification/today])
      (same-calendar-day? created yesterday) (t [:notification/yesterday])
      :else (shared.time/format-date created-at {}))))

(defn- relative-time
  [created-at]
  (let [diff-min (js/Math.floor (/ (- (.getTime (js/Date.)) (.getTime (->date created-at))) 60000))
        diff-hours (js/Math.floor (/ diff-min 60))
        diff-days (js/Math.floor (/ diff-hours 24))]
    (cond
      (< diff-min 1) (t [:notification/just-now])
      (= diff-min 1) (t [:notification/minute-ago])
      (< diff-min 60) (t [:notification/minutes-ago] [diff-min])
      (= diff-hours 1) (t [:notification/hour-ago])
      (< diff-hours 24) (t [:notification/hours-ago] [diff-hours])
      (= diff-days 1) (t [:notification/day-ago])
      :else (t [:notification/days-ago] [diff-days]))))

(defn- group-by-day
  "Groups already date-sorted `notifications` into `{:label _ :items _}`
  buckets, preserving chronological order (so e.g. all `Hoy` notifications
  stay together instead of being scattered by hash-map iteration order)."
  [notifications]
  (reduce
   (fn [groups {:keys [created-at] :as notification}]
     (let [label (day-group-label created-at)]
       (if (= label (:label (peek groups)))
         (update-in groups [(dec (count groups)) :items] conj notification)
         (conj groups {:label label :items [notification]}))))
   []
   (sort-by (comp jt.instant/to-epoch-milli :created-at) > notifications)))

(defn- navigate-to-action!
  [{:keys [route path-params]}]
  (when route
    (rf/dispatch [::nav/push-state [(keyword route)] path-params])))

(defn- notification-row
  [{:keys [id message created-at trigger is-read action]}]
  ^{:key id}
  [:div.notification-bell-panel__item
   {:class [(when-not is-read "notification-bell-panel__item--unread")
            (when (:route action) "notification-bell-panel__item--clickable")]
    :on-click (fn [_]
                (when-not is-read
                  (rf/dispatch [::srv.notification/mark-notification-read id]))
                (navigate-to-action! action))}
   [:div.notification-bell-panel__item-icon-box
    [layout.icon/icon {:key (icon-key-for-trigger trigger)
                       :class ["notification-bell-panel__item-icon"]}]
    (when-not is-read
      [:span.notification-bell-panel__item-dot])]
   [:div.notification-bell-panel__item-content
    [:div.notification-bell-panel__item-message (decode-message message)]
    [:div.notification-bell-panel__item-time (relative-time created-at)]]])

(defn- notification-group
  [{:keys [label items]}]
  ^{:key label}
  [:div.notification-bell-panel__group
   [:h3.notification-bell-panel__group-label label]
   (map notification-row items)])

(defn- panel
  [opts _args _children]
  (let [notifications @(rf/subscribe [::entity-list/list entity-key])]
    [:div (update opts :className str " notification-bell-panel")
     (if (seq notifications)
       (map notification-group (group-by-day notifications))
       [:div.notification-bell-panel__empty (t [:notification/empty])])]))

(defn- bell-button
  [opts unread?]
  [:button.notification-bell opts
   [layout.icon/icon {:key :notification-bell}]
   (when unread?
     [:span.notification-bell__dot])])

(defn main
  []
  (let [unread? (pos? (or @(rf/subscribe [::srv.notification/unread-count]) 0))]
    [:> layout.dropdown-menu/menu
     {:placement "bottom-end"
      :reference {:component bell-button
                  :args (util.interop/->js-interop-data-envelope unread?)}
      :floating {:component panel
                 :onOpen (util.interop/->js-interop-data-envelope
                          {:evt [::entity-list/load-list
                                 {:entity-key entity-key
                                  :uri "/api/notifications"
                                  :extra-params {:pagination {:page 0 :page-size 20}}}]})}}]))
